import { isEmptyString } from '@vy/string';
import type { Row, Workbook, Worksheet } from 'exceljs';
import type { CustomerMasterExportRow } from '../customer-master-report-hooks/flatten-customer-master-for-export';

const SHEET_NAME = 'Customer master';
const MIN_COLUMN_WIDTH = 14;
const COLUMN_WIDTH_PADDING = 4;

const DATE_COLUMN_KEYS = new Set(['createdAt', 'startDate', 'endDate', 'syncStatusUpdatedAt']);
const CURRENCY_COLUMN_KEYS = new Set(['estimatedAmount', 'mrr']);

const DATE_NUMBER_FORMAT = 'yyyy-mm-dd';
const CURRENCY_NUMBER_FORMAT = '"$"#,##0.00;[Red]"-$"#,##0.00';

type HyperlinkSpec = { rowIdKey: string; buildUrl: (id: string) => string };
const HYPERLINK_SPECS: Record<string, HyperlinkSpec> = {
  customerName: {
    rowIdKey: 'customerId',
    buildUrl: (id) => `${window.location.origin}/customers/${id}/overview`,
  },
  contractName: {
    rowIdKey: 'contractId',
    buildUrl: (id) => `${window.location.origin}/plans/contracts/${id}`,
  },
};

type BuildCustomerMasterExportInput = {
  rows: CustomerMasterExportRow[];
};

// Headers are the union of keys across all rows so dynamic custom fields render
// alongside the built-in columns without an upfront schema.
export function buildCustomerMasterExport(
  workbook: Workbook,
  input: BuildCustomerMasterExportInput,
): void {
  for (const worksheet of workbook.worksheets) {
    workbook.removeWorksheet(worksheet.id);
  }

  const headers = collectHeaders(input.rows);
  const worksheet = workbook.addWorksheet(SHEET_NAME);

  worksheet.addRow(headers);
  styleHeaderRow(worksheet);

  for (const row of input.rows) {
    const cells = headers.map((header) => row[header] ?? null);
    const excelRow = worksheet.addRow(cells);
    applyHyperlinks(excelRow, headers, row);
  }

  worksheet.views = [{ state: 'frozen', ySplit: 1 }];
  applyColumnFormats(worksheet, headers);
  autoSizeColumns(worksheet, headers);
}

function collectHeaders(rows: CustomerMasterExportRow[]): string[] {
  const seen = new Set<string>();
  const ordered: string[] = [];
  for (const row of rows) {
    for (const key of Object.keys(row)) {
      if (seen.has(key)) continue;
      seen.add(key);
      ordered.push(key);
    }
  }
  return ordered;
}

function styleHeaderRow(worksheet: Worksheet): void {
  const headerRow = worksheet.getRow(1);
  headerRow.font = { bold: true };
  headerRow.alignment = { vertical: 'middle', horizontal: 'left' };
  headerRow.commit();
}

function applyColumnFormats(worksheet: Worksheet, headers: string[]): void {
  headers.forEach((header, index) => {
    const column = worksheet.getColumn(index + 1);
    if (DATE_COLUMN_KEYS.has(header)) {
      column.numFmt = DATE_NUMBER_FORMAT;
      return;
    }
    if (CURRENCY_COLUMN_KEYS.has(header)) {
      column.numFmt = CURRENCY_NUMBER_FORMAT;
    }
  });
}

function applyHyperlinks(excelRow: Row, headers: string[], sourceRow: CustomerMasterExportRow): void {
  headers.forEach((header, index) => {
    const spec = HYPERLINK_SPECS[header];
    if (!spec) return;

    const displayValue = sourceRow[header];
    if (typeof displayValue !== 'string' || isEmptyString(displayValue)) return;

    const idValue = sourceRow[spec.rowIdKey];
    if (typeof idValue !== 'string' || isEmptyString(idValue)) return;

    excelRow.getCell(index + 1).value = {
      text: displayValue,
      hyperlink: spec.buildUrl(idValue),
      tooltip: `Open ${displayValue} in Vayu`,
    };
  });
}

function autoSizeColumns(worksheet: Worksheet, headers: string[]): void {
  headers.forEach((header, index) => {
    worksheet.getColumn(index + 1).width = Math.max(MIN_COLUMN_WIDTH, header.length + COLUMN_WIDTH_PADDING);
  });
}
